import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApp } from '@/contexts/AppContext';
import { useIndustries } from '@/hooks/useIndustries';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { isValidGoogleUrl } from '@/lib/urlValidation';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, authLoading, business, createBusiness } = useApp();
  const { industries, loading: industriesLoading, getCategoriesForIndustry } = useIndustries();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    category: '',
    location: '',
    service_area: '',
    google_profile_url: '',
    logo_url: null as string | null,
    services: [] as string[],
  });

  // Redirect if not authenticated or already has business
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (business) {
        navigate('/dashboard');
      }
    }
  }, [user, authLoading, business, navigate]);

  const businessCategories = getCategoriesForIndustry(formData.industry);

  const handleChange = (field: string, value: string | string[]) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleIndustryChange = (value: string) => {
    setFormData({ ...formData, industry: value, category: '' });
  };

  const handleSubmit = async () => {
    // Validate Google Profile URL if provided
    if (formData.google_profile_url && !isValidGoogleUrl(formData.google_profile_url)) {
      toast.error('Please enter a valid Google Business Profile URL (e.g., https://g.page/your-business)');
      return;
    }
    
    setLoading(true);
    const { error } = await createBusiness(formData);
    setLoading(false);
    
    if (!error) {
      navigate('/dashboard');
    }
  };

  if (authLoading || industriesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 mx-auto rounded-full bg-primary/20 flex items-center justify-center animate-pulse-soft">
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Step {step} of 2</span>
            <span className="text-sm text-muted-foreground">{step * 50}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${step * 50}%` }}
            />
          </div>
        </div>

        {/* Card */}
        <div className="soft-card">
          <h1 className="text-xl font-semibold mb-6">
            {step === 1 ? 'Business Information' : 'Service Details'}
          </h1>

          {step === 1 ? (
            <div className="space-y-5">
              {/* Business Name */}
              <div>
                <Label className="modern-label">Business Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Your Business Name"
                  className="modern-input"
                />
              </div>

              {/* Industry Dropdown */}
              <div>
                <Label className="modern-label">Industry</Label>
                <Select value={formData.industry} onValueChange={handleIndustryChange}>
                  <SelectTrigger className="modern-input bg-background">
                    <SelectValue placeholder="Select Industry" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border border-border shadow-lg z-50">
                    {industries.map((ind) => (
                      <SelectItem key={ind.id} value={ind.name} className="cursor-pointer">
                        {ind.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Business Category */}
              <div>
                <Label className="modern-label">Business Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => handleChange('category', value)}
                  disabled={!formData.industry}
                >
                  <SelectTrigger className="modern-input bg-background">
                    <SelectValue placeholder={formData.industry ? "Select Category" : "Select Industry first"} />
                  </SelectTrigger>
                  <SelectContent className="bg-background border border-border shadow-lg z-50">
                    {businessCategories.map((cat) => (
                      <SelectItem key={cat} value={cat} className="cursor-pointer">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location */}
              <div>
                <Label className="modern-label">Location (City, State)</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="New York, NY"
                  className="modern-input"
                />
              </div>

              <Button 
                onClick={() => setStep(2)} 
                className="gradient-btn w-full mt-6 h-12"
                disabled={!formData.name || !formData.industry || !formData.category || !formData.location}
              >
                Continue
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Service Area */}
              <div>
                <Label className="modern-label">Service Area</Label>
                <Input
                  value={formData.service_area}
                  onChange={(e) => handleChange('service_area', e.target.value)}
                  placeholder="e.g., Manhattan, Brooklyn"
                  className="modern-input"
                />
              </div>

              {/* Google Business Profile URL */}
              <div>
                <Label className="modern-label">Google Business Profile URL (optional)</Label>
                <Input
                  value={formData.google_profile_url}
                  onChange={(e) => handleChange('google_profile_url', e.target.value)}
                  placeholder="https://g.page/your-business"
                  className="modern-input"
                />
              </div>

              {/* Primary Services */}
              <div>
                <Label className="modern-label">Primary Services (comma-separated)</Label>
                <Input
                  value={formData.services.join(', ')}
                  onChange={(e) => handleChange('services', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  placeholder="Consultation, Treatment, Follow-up"
                  className="modern-input"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-12">
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  className="gradient-btn flex-1 h-12"
                  disabled={loading || !formData.service_area}
                >
                  {loading ? 'Creating...' : 'Complete Setup'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
